import { Component, OnDestroy, computed, effect, inject, signal } from '@angular/core';

import { NvButton } from '../../ui/button/nv-button';
import { NvToastService } from '../../ui/toast/nv-toast';
import { AlarmService } from '../../../core/notifications/alarm.service';
import { SpeechService } from '../../../core/notifications/speech.service';
import { PendingRequestsService } from '../../../core/notifications/pending-requests.service';
import { OrdersService } from '../../services/orders.service';
import { OrdersStore } from '../../../core/stores/orders.store';
import { FloorStore } from '../../../core/stores/floor.store';
import { Order } from '../../models/order.model';
import { toMillis } from '../../utils/timestamp.util';
import { NvDialogService } from '../../ui/dialog/nv-dialog.service';
import { OrderApprovalFlow } from '../../services/order-approval-flow.service';

/** How many arrivals in one batch get announced by name before the rest are summarised — mirrors RequestAlerts' own cap. */
const MAX_SPOKEN_PER_BATCH = 3;

/** How often an unapproved order's alert repeats — measured from the oldest still-pending order's own age, same escalation shape as RequestAlerts. */
const ROUND_MS = 60_000;
/** Rounds 0–2 are bounded tune+speech+fade cycles. Round 3 — three minutes unapproved — is where the continuous warning kicks in; an unconfirmed order left that long is exactly as urgent as an unaddressed guest request. */
const CONTINUOUS_AT_ROUND = 3;
/** Once continuous, the spoken line repeats on its own 30-second cadence — decoupled from ROUND_MS, which would otherwise leave a manager hearing nothing but the tune for up to a minute at a time during the most urgent phase. */
const CONTINUOUS_SPEECH_MS = 30_000;
/** Fine enough that a round boundary is never more than this late, coarse enough not to burn a timer for nothing. */
const TICK_MS = 5_000;

/**
 * Guest orders cannot go through the shared `notifications` bus (they're a
 * different collection with a different shape), so this is its own live
 * listener on orders where status == 'pending_approval' — without it, a
 * guest taps "place order" and nothing visibly happens anywhere on staff
 * screens (Stage 4, docs/build-plan-menu-orders-kot-billing.md). Same
 * "always mounted above the router outlet" placement as RequestAlerts.
 *
 * Escalation now mirrors RequestAlerts exactly (round 0 on arrival, bounded
 * repeats at 1 and 2 minutes, continuous alarm from 3 minutes unapproved)
 * rather than the single 4-second nudge this used to be — a guest's order
 * sitting unconfirmed is exactly as urgent as an unaddressed water request,
 * and staff going quiet on a live table for minutes is the failure mode the
 * old bounded round didn't cover at all.
 */
@Component({
  selector: 'nv-order-approval-strip',
  imports: [NvButton],
  templateUrl: './order-approval-strip.html',
  styleUrl: './order-approval-strip.scss',
})
export class OrderApprovalStrip implements OnDestroy {
  // Still injected directly for its write methods (approveOrder, rejectOrder)
  // — only the read below moved to OrdersStore.
  private readonly orders = inject(OrdersService);
  private readonly ordersStore = inject(OrdersStore);
  private readonly floor = inject(FloorStore);
  private readonly toasts = inject(NvToastService);
  private readonly dialog = inject(NvDialogService);
  private readonly alarm = inject(AlarmService);
  private readonly speech = inject(SpeechService);
  private readonly pendingRequests = inject(PendingRequestsService);
  private readonly approvalFlow = inject(OrderApprovalFlow);

  readonly pending = this.ordersStore.pendingApproval;

  /** The venue's own table numbers, keyed by table id — every order.tableId interpolation below goes through this rather than printing the raw Firestore document id (see table-requests-list.ts's identical join, the pattern this mirrors). */
  private readonly tableNumbers = computed(() => {
    const map = new Map<string, number>();
    for (const table of this.floor.tables()) map.set(table.id, table.number);
    return map;
  });

  tableLabel(tableId: string): string {
    const number = this.tableNumbers().get(tableId);
    return number !== undefined ? String(number) : tableId;
  }

  readonly ordered = computed(() =>
    [...this.pending()].sort((a, b) => (toMillis(a.createdAt) ?? 0) - (toMillis(b.createdAt) ?? 0))
  );

  readonly count = computed(() => this.ordered().length);

  private readonly now = signal(Date.now());
  private readonly tickTimer: ReturnType<typeof setInterval>;
  /** Order ids already seen, so a re-render doesn't read as a fresh arrival. */
  private seen = new Set<string>();
  /** Order ids already spoken as a *new arrival* — repeat-round announcements don't consult this, since "still waiting" is meant to be said again. */
  private announcedFor = new Set<string>();

  /** The last round number a cycle actually fired for, so a re-render mid-round doesn't restart it — same re-entrancy shape as RequestAlerts. */
  private lastFiredRound = -1;
  private continuousActive = false;
  /** When continuous mode last spoke — its own CONTINUOUS_SPEECH_MS cadence, independent of ROUND_MS. */
  private continuousSpeechAt = 0;
  private busy = false;
  private dirty = false;

  /** A background/inactive tab gets its setInterval throttled by the browser (Chrome: as coarse as once a minute), so a manager who tabs away for a few minutes can genuinely miss a round or two — see onVisible's own note. */
  private readonly onVisible = (): void => {
    if (document.visibilityState === 'visible') this.now.set(Date.now());
  };

  constructor() {
    this.tickTimer = setInterval(() => this.now.set(Date.now()), TICK_MS);
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', this.onVisible);
    }

    effect(() => {
      this.pending();
      this.now();
      this.kick();
    });
  }

  ngOnDestroy(): void {
    clearInterval(this.tickTimer);
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.onVisible);
    }
    this.alarm.stop();
    this.alarm.endRound();
    this.speech.cancel();
  }

  /** Re-entrant guard: a trigger that arrives mid-round just asks the in-flight run to loop again rather than starting a second, overlapping one. */
  private kick(): void {
    if (this.busy) {
      this.dirty = true;
      return;
    }
    this.busy = true;
    void this.loop();
  }

  private async loop(): Promise<void> {
    try {
      do {
        this.dirty = false;
        await this.runOnce();
      } while (this.dirty);
    } catch (err) {
      console.error('OrderApprovalStrip: alert round failed, will retry on the next trigger', err);
    } finally {
      this.busy = false;
    }
  }

  private async runOnce(): Promise<void> {
    const pending = this.pending();

    if (pending.length === 0) {
      this.alarm.stop();
      this.alarm.endRound();
      this.announcedFor.clear();
      this.lastFiredRound = -1;
      this.continuousActive = false;
      this.continuousSpeechAt = 0;
      return;
    }

    const ids = pending.map((order) => order.id);
    const hasNew = ids.some((id) => !this.seen.has(id));
    this.seen = new Set(ids);

    const oldestMs = this.oldestPendingAt(pending);
    const round = oldestMs === null ? 0 : Math.max(0, Math.floor((this.now() - oldestMs) / ROUND_MS));
    const isNewRound = round !== this.lastFiredRound;

    if (round >= CONTINUOUS_AT_ROUND) {
      // Continuous mode runs on its own clock from here — every tick (not
      // just round boundaries, which are a full minute apart) re-arms the
      // tune (AlarmService.start()'s doc comment on why a silently-paused
      // one would otherwise go unnoticed) and speaks again once
      // CONTINUOUS_SPEECH_MS has passed, so a manager is never more than 30
      // seconds from a reminder during the most urgent phase.
      if (!this.continuousActive) {
        this.continuousActive = true;
        this.continuousSpeechAt = 0; // speak immediately on entry
      }
      this.lastFiredRound = round;
      this.alarm.start();
      const due = this.now() - this.continuousSpeechAt >= CONTINUOUS_SPEECH_MS;
      if (hasNew || due) {
        this.continuousSpeechAt = this.now();
        await this.speakContinuous(pending);
      }
      return;
    }

    if (!hasNew && !isNewRound) return;
    this.lastFiredRound = round;

    if (this.continuousActive) {
      this.continuousActive = false;
      this.alarm.stop();
    }
    this.alarm.playRound();
    try {
      await this.speakFor(pending, hasNew);
    } finally {
      this.alarm.endRound();
    }
  }

  /**
   * Continuous mode's own announcement — deliberately not per-order: several
   * orders all past three minutes unapproved would otherwise queue up as
   * separate utterances that overlap or talk over each other every 30
   * seconds. One table gets named (there's nothing to disambiguate); more
   * than one collapses to a single generic line naming the count instead.
   */
  private async speakContinuous(pending: Order[]): Promise<void> {
    if (this.alarm.muted() || !this.speech.supported) return;
    this.alarm.duck();
    try {
      const message =
        pending.length > 1
          ? 'Unattended orders. Please acknowledge immediately.'
          : `Table ${this.tableLabel(pending[0].tableId)}'s order is still waiting. Please acknowledge immediately.`;
      await this.speech.announce(message);
    } finally {
      this.alarm.unduck();
    }
  }

  private async speakFor(pending: Order[], hasNew: boolean): Promise<void> {
    if (this.alarm.muted() || !this.speech.supported) {
      this.announcedFor = new Set(pending.map((order) => order.id));
      return;
    }
    this.alarm.duck();
    try {
      if (hasNew) {
        await this.announceNewArrivals(pending);
      } else {
        await this.announceStillWaiting(pending);
      }
    } finally {
      this.alarm.unduck();
    }
  }

  private oldestPendingAt(pending: Order[]): number | null {
    let oldest: number | null = null;
    for (const order of pending) {
      const at = toMillis(order.createdAt);
      if (at !== null && (oldest === null || at < oldest)) oldest = at;
    }
    return oldest;
  }

  /** Speaks each newly placed order once — "Order placed for table 16. Waiting for your confirmation." — over the top of the round's tune. */
  private async announceNewArrivals(pending: Order[]): Promise<void> {
    const fresh = pending.filter((order) => !this.announcedFor.has(order.id));
    const spoken = fresh.slice(0, MAX_SPOKEN_PER_BATCH);
    for (const order of spoken) {
      await this.speech.announce(`Order placed for table ${this.tableLabel(order.tableId)}. Waiting for your confirmation.`);
    }

    const remaining = fresh.length - spoken.length;
    if (remaining > 0) {
      await this.speech.announce(
        remaining === 1 ? 'And one more order waiting' : `And ${remaining} more orders waiting`
      );
    }

    this.announcedFor = new Set(pending.map((order) => order.id));
  }

  /** A round boundary with nothing new — repeats the oldest unconfirmed order rather than the whole backlog. */
  private async announceStillWaiting(pending: Order[]): Promise<void> {
    const oldest = [...pending].sort(
      (a, b) => (toMillis(a.createdAt) ?? 0) - (toMillis(b.createdAt) ?? 0)
    )[0];
    if (!oldest) return;
    const extra = pending.length > 1 ? ` and ${pending.length - 1} more` : '';
    await this.speech.announce(
      `Table ${this.tableLabel(oldest.tableId)}'s order is still waiting for your confirmation${extra}`
    );
  }

  /**
   * The item picker, opened via the row's "Show items" button — always
   * shows the guest's order before committing to anything. Reject lives as
   * its own sibling button on the row (see the template), not inside this
   * dialog — a manager rejecting an order outright over kitchen timing
   * shouldn't have to open the item list first to find it.
   * "Partial approval" in the name undersells it: cancelling the dialog
   * approves nothing, leaving every line ticked approves the whole order
   * via the exact same server path a blind approve used to.
   */
  /**
   * Both of these now just hand off to OrderApprovalFlow — the floor card's
   * own Review order button (new issue #4) runs the identical flow, and
   * "the same action as the task in the panel" has to stay literally true
   * rather than becoming two copies that drift apart.
   */
  async approveSome(order: Order): Promise<void> {
    await this.approvalFlow.review(order);
  }

  async reject(order: Order): Promise<void> {
    await this.approvalFlow.reject(order);
  }
}
